import React, { useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DescriptionOutlined } from '@mui/icons-material';
import config from '../../../config';

interface MemberSummaryCardProps {
  member: any;
  roleType: 'director' | 'beneficialOwner' | 'authRep';
}

const MemberSummaryCard: React.FC<MemberSummaryCardProps> = ({ member, roleType }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const getDocumentUrl = (rawUrl: any) => {
    let url = rawUrl;
    if (url && typeof url === 'string' && !url.startsWith('data:image') && !url.startsWith('http://') && !url.startsWith('https://')) {
      const name = url.includes('/') ? url.split('/').pop() : url;
      url = `${config.NODE_API_BASE_URL_V2}/file/${name}`;
    } else if (url && typeof url === 'string' && !url.startsWith('data:image') && url.includes('/file/')) {
      const name = url.split('/file/').pop();
      url = `${config.NODE_API_BASE_URL_V2}/file/${name}`;
    }
    return url;
  };

  const identityDocUrl = member.identityDocumentUrl ? getDocumentUrl(member.identityDocumentUrl) : '';

  // Determine the display role title
  let displayRole = '';
  if (roleType === 'director') {
    displayRole = member.officialTitle || 'Director';
  } else if (roleType === 'beneficialOwner') {
    displayRole = 'Beneficial Owner' + (member.percentageOfOwnership ? ` (${member.percentageOfOwnership}%)` : '');
  } else if (roleType === 'authRep') {
    displayRole = 'Authorized Representative';
  }

  const name = `${member.firstName || ''} ${member.middleName || ''} ${member.lastName || ''}`.replace(/\s+/g, ' ').trim();

  return (
    <div 
      className="p-3 mb-3 bg-white rounded-4 shadow-sm" 
      style={{ cursor: 'pointer', border: '1px solid #f0f0f0', transition: 'all 0.2s ease-in-out' }}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <h6 className="fw-bold mb-1" style={{ fontSize: '16px', color: '#1a1a1a' }}>{name}</h6>
          <div style={{ color: '#00D084', fontSize: '14px', marginBottom: '4px' }}>{displayRole}</div>
          <div className="text-muted" style={{ fontSize: '13px' }}>{member.email}</div>
        </div>
        <div>
          <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} text-muted`} style={{ fontSize: '14px', marginTop: '5px' }}></i>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 pt-3 border-top">
          <Row className="mb-3">
            <Col md={4} className="mb-2">
              <label className="text-muted small mb-1">Date of Birth</label>
              <div className="fw-semibold" style={{ fontSize: '14px' }}>{member.dateOfBirth || '-'}</div>
            </Col>
            <Col md={8} className="mb-2">
              <label className="text-muted small mb-1">Address</label>
              <div className="fw-semibold" style={{ fontSize: '14px' }}>
                {[member.streetAddress, member.city, member.state, member.postalCode, member.country].filter(Boolean).join(', ') || '-'}
              </div>
            </Col>
          </Row>

          {roleType === 'beneficialOwner' && member.percentageOfOwnership > 20 && (
            <Row className="mb-3">
              <Col md={4} className="mb-2">
                <label className="text-muted small mb-1">Place of Birth</label>
                <div className="fw-semibold" style={{ fontSize: '14px' }}>{member.placeOfBirth || '-'}</div>
              </Col>
              <Col md={4} className="mb-2">
                <label className="text-muted small mb-1">Nationality</label>
                <div className="fw-semibold" style={{ fontSize: '14px' }}>{member.nationality || '-'}</div>
              </Col>
              <Col md={4} className="mb-2">
                <label className="text-muted small mb-1">Country of Residence</label>
                <div className="fw-semibold" style={{ fontSize: '14px' }}>{member.countryOfResidence || '-'}</div>
              </Col>
            </Row>
          )}

          {identityDocUrl && (
            <Row>
              <Col md={12}>
                <label className="text-muted small mb-1">Identity Document</label>
                <div 
                  className="bg-sky pb-1 d-flex flex-column align-items-center mb-2 rounded-3 py-3 mt-1" 
                  style={{ cursor: 'pointer', width: '150px' }} 
                  onClick={() => window.open(identityDocUrl, '_blank')}
                >
                  <DescriptionOutlined sx={{ fontSize: 40 }} style={{ color: "#00D084" }} />
                  <p className="small mt-2 mb-0 text-secondary fw-semibold">View Document</p>
                </div>
              </Col>
            </Row>
          )}
        </div>
      )}
    </div>
  );
};

export default MemberSummaryCard;
